/* Dynamic Plot code copyright 2015-2020 (c) Tuva Labs Inc. */
import ContextMenuUtil from "../../../utils/ContextMenuUtil";
import { PlotElement } from "./plotElement";

/**
 * Overlay plot elements: LeastSquaresElement, etc
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by jpatel and cmiller
 *
 * 2017-06-05 (CDM) FreehandLineElement moved to annotateElement.js
 *                  XRefLineElement, YRefLineElement, DiagRefLineElement moved to refLineElement.js
 */

// import {Dyn} from '../../dynDataSet/dynMiscellany';
// import mediator from '../../dynDataSet/mediatorUtil';

export const wrapDynLeastSquaresElement = function(Dyn) {
    let mediator = Dyn.mediator;

//============================

    class LeastSquaresLineModel {
        // optional this.normalizedCoords (otherwise use default, see _labelPositionToModel)
        // optional this.lineColor if custom line color (otherwise use default from _getLineStrokeColor())
        constructor( xAttrIndex, yAttrIndex, categoryIndex, leastSquares, lineIndex ) {
            this.xAttrIndex = xAttrIndex; // index of attribute on X-axis
            this.yAttrIndex = yAttrIndex; // index of attribute on X-axis
            this.categoryIndex = categoryIndex; // index of category of selected/legend attribute, if any
            this.data = leastSquares; // .slope, etc.
            this.id = "LSL-"+(lineIndex+1);
        }
    }

    /**
     * Dyn.LeastSquaresElement()
     */
    class LeastSquaresElement extends PlotElement {

        constructor( Dyn, toggleControlID ) {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityLeastSquares,
                replaceType: Dyn.EPlotControl.kControlsLeastSquares,
                primaryType: Dyn.EPlotControl.kControlsLeastSquares,
                otherControls: [
                    Dyn.EPlotControl.kControlsLeastSquaresByAttribute,
                    Dyn.EPlotControl.kControlsShowSquares
                ]
                // Dyn.EPlotControl.kControlsMargins ] // NO LONGER adjust the margins when this element added/removed
                // note: can't use kControlsAdornmentPositionOnly because kControlsMargins overrides
            }, Dyn );
            // if provided a control type, only turn on that plot option
            if( toggleControlID ) {
                // turn off all but the given control
                this[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] = (toggleControlID === Dyn.EPlotControl.kControlsLeastSquaresByAttribute);
                this[Dyn.EPlotControl.kControlsLeastSquaresByCategory] = (toggleControlID === Dyn.EPlotControl.kControlsLeastSquaresByCategory);
            } else {
                // explicitly turn these on/off by default
                this[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] = true;
                this[Dyn.EPlotControl.kControlsLeastSquaresByCategory] = false;
            }

            this.selectedLineIndex = -1; // index into this.getAdornmentsArray() or -1 if nothing selected
            this.showSquaresLineIndex = -1; // index into this.getAdornmentsArray() or -1 if no lines showing squares
            //this.subscriptions = [];
            this.lineColors = [];
            this.lineModels = [];
            // optional this.normalizedCoords (otherwise use default, see _labelPositionToModel)

            // Listen for category select event to highlight the selected category from summary view
            // Dyn.mediatorLocalSubscribe( this.subscriptions, "summary:categorySelected", ( data ) => {
            //     var selectedAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
            //         leastSqAdorns = this.getAdornmentsArray();
            //     if( leastSqAdorns && leastSqAdorns.length > 0
            //         && data.attributeID === selectedAttribute.id ){
            //         var categoryIndex = selectedAttribute.stats.categories.indexOf( data.category );
            //         this.selectAdornment( categoryIndex );
            //     }
            // } );
        }

    get kUndoAction() { return 'Least Squares Line'; }
    get kNumInitialAdornments() { return 1; } // menu button

    destroy() {
        // Dyn.mediatorLocalRemoveAll( this.subscriptions ); // unsubscribe from "summary:categorySelected"
        // if removing the line that is showing squares, find a new line to show squares, if any
        //  Note that this element has already been removed from the plot so its lines will not be considered.
        this.plotShaper.showHideSquaresOnLines( this.plotShaper.showSquaresOnLines, null );
        super.destroy();
    }

    /**
     * Save the PlotElement to a data object that can be JSONified.
     * @returns {{}} - data to be used by restore()
     */
    save() {
        // get PlotElement base model
        let plotElementModel = super.save();

        // save toggle values
        plotElementModel[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] = this[Dyn.EPlotControl.kControlsLeastSquaresByAttribute];
        plotElementModel[Dyn.EPlotControl.kControlsLeastSquaresByCategory] = this[Dyn.EPlotControl.kControlsLeastSquaresByCategory];

        if( this.normalizedCoords ){
            plotElementModel.normalizedCoords = [];
            this.normalizedCoords.forEach( coords => {
                plotElementModel.normalizedCoords.push( {
                    ox: coords.ox,
                    oy: coords.oy
                } );
            } );
        }
        if( this.lineColors ){
            plotElementModel.lineColors = Dyn.dataUtil.clone( this.lineColors );
        }
        plotElementModel.showSquaresLineIndex = this.showSquaresLineIndex;

        return plotElementModel;
    }

    /**
     * Change our PlotElement to match the saved data.
     */
    restore( savedData ) {
        // restore base model
        super.restore( savedData );

        // restore toggleControl() flags
        this[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] = Boolean( savedData[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] );
        this[Dyn.EPlotControl.kControlsLeastSquaresByCategory] = Boolean( savedData[Dyn.EPlotControl.kControlsLeastSquaresByCategory] );

        // restore saved positions (of user-draggable stat labels)
        if( savedData.normalizedCoords ){
            this.normalizedCoords = this.normalizedCoords || [];
            savedData.normalizedCoords.forEach( coords => {
                this.normalizedCoords.push( {
                    ox: coords.ox,
                    oy: coords.oy
                } );
            } );
        }
        if( savedData.lineColors ){
            this.lineColors = Dyn.dataUtil.clone( savedData.lineColors );
        }
        this.showSquaresLineIndex = Number.isFinite( savedData.showSquaresLineIndex ) ? savedData.showSquaresLineIndex : -1;
    }

    /** save label position coordinates for restore, if any */
    labelPositionToModel( textElement ) {
        let labelOffset = textElement.data( "offset" ),
            lineIndex = textElement.data( "lineIndex" );
        if( !this.normalizedCoords ){
            this.normalizedCoords = [];
        }
        if( labelOffset ){
            // save relative position center of user-dragged label from Least Squares Line
            this.normalizedCoords[lineIndex] = {
                ox: labelOffset.x.n,
                oy: labelOffset.y.n,
            };
            //console.log( "LeastSquaresElement::labelPositionToModel", labelOffset.x.n, labelOffset.y.n );
        }
    }

    /** restore any normalized positions that were saved, else do nothing */
    modelToLabelPosition( textElement, cellRect, lineIndex ) {
        let labelOffset = textElement.data( "offset" ) || {};
        if( this.normalizedCoords && (this.normalizedCoords.length > lineIndex) ){
            // restore the relative offset from widget to user-dragged label,
            //  which is used by PlotLabelsUtil.restoreLabelPositionAndOffset()
            let nc = this.normalizedCoords[lineIndex];
            labelOffset.x = new Dyn.axisUtil.NACoord( nc.ox );
            labelOffset.y = new Dyn.axisUtil.NACoord( nc.oy );
            textElement.data( "offset", labelOffset );
            textElement.data( "cellRect", cellRect );
            //console.log("LeastSquaresElement::modelToLabelPosition", this.normalizedCoords);
        }
    }

    /** modify the control type in this plot element, to turn on and off features */
    toggleControl( toggleControlID ) {
        "use strict";
        var controlValue = this[toggleControlID];
        Dyn.assert( typeof controlValue === "boolean", "toggleControl() could not find " + toggleControlID );

        // only modify control types on our list of allowable changes.
        switch( toggleControlID ){
            case Dyn.EPlotControl.kControlsLeastSquaresByAttribute:
            case Dyn.EPlotControl.kControlsLeastSquaresByCategory:
                this[toggleControlID] = !controlValue;
                if( toggleControlID !== Dyn.EPlotControl.kControlsLeastSquaresByAttribute ){
                    this[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] = false; // turn off other radio buttons
                }
                if( toggleControlID !== Dyn.EPlotControl.kControlsLeastSquaresByCategory ){
                    this[Dyn.EPlotControl.kControlsLeastSquaresByCategory] = false; // turn off other radio buttons
                }
                break;
        }
    }

    /**
     * Return true if this element has all toggle elements turned off, and thus can be removed from the plot.
     * @returns {boolean}
     */
    isElementWithAllTogglesOff() {
        "use strict";
        return !(
            this[Dyn.EPlotControl.kControlsLeastSquaresByAttribute] ||
            this[Dyn.EPlotControl.kControlsLeastSquaresByCategory]
        );
    }

    /** Update the plot's top margin to add in a subtitle, or remove the subtitle  */
    calcAxis( ioWhatChanged, subViewRef ) {
        "use strict";
        //var useSubMargin = true; // or not
        //subViewRef.topMargin.showHideSubtitle( useSubMargin );
    }

    /**
     * Update adornments (specific to this element) to show a least-squares regression line in the plot.
     * The maximum number of regression lines this element controls is:
     *      (number of cells * number of numeric attributes on one axis * number of categories in legend )
     */
    calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
        "use strict";

        var leastSqAdorns = ioAdornArray,
            plotViewPaper = this.plotShaper.adornments.paper,
            plotElement = this,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            newNumLines = 0;

        measuresArray.length = 0; // remove old measures (values for summary table)
        this.lineModels.length = 0; // remove old line models

        function targetIsTextLabel( e ) {
            return (typeof e.target.textContent === "string") && (e.target.textContent.length > 0);
        }

        function hoverIn( e ) {
            let lineSet = this, // this === lineSet, e.target === string ((textLabel) or path (wrapperCover))
                lineElement = lineSet.getLineElement(),
                textElement = lineSet.getNonOverlappingLabel(),
                lineColor = lineElement.data( "color" ), // saved non-hover/non-animated color
                hoverColor = Dyn.PlotLabelsUtil.getHoverColorFromLabelColor( lineColor );
            if( targetIsTextLabel( e ) ){
                textElement.attr( "cursor", Dyn.plotUtil.cursorMove );
            } else if( Dyn.toolTip.isMyElement( lineElement ) ){
                Dyn.toolTip.update( lineElement, e, {} );
            } else {
                Dyn.toolTip.show( lineElement, e, {} );
            }
            plotElement._updateLineColor( lineSet, hoverColor );
            plotElement.addGlow( lineElement, lineColor, Dyn.plotUtil.lineGlowWidth );
        }

        function hoverOut( /* e  */ ) {
            let lineSet = this, // this === lineSet, e.target === string ((textLabel) or path (wrapperCover))
                lineElement = lineSet.getLineElement(),
                lineColor = lineElement.data( "color" ); // saved non-hover/non-animated color
            if( Dyn.toolTip.isMyElement( lineElement ) ){ // tip element from hoverIn()
                Dyn.toolTip.hide();
            }
            plotElement._updateLineColor( lineSet, lineColor );
            plotElement.removeGlow();
        }

        function mouseMove( e ) {
            // update tooltip position as mouse moves over the line
            let wrapperCover = this;
            if( Dyn.toolTip.isMyElement( wrapperCover ) ){
                Dyn.toolTip.update( wrapperCover, e, {} );
            }
        }

        function lineClick( e ) {
            let lineSet = this,
                textElement = lineSet.getNonOverlappingLabel(),
                lineIndex = textElement.data( "lineIndex" );
            plotElement.selectAdornment( lineIndex );
            Dyn.mediator.publish( "plot:updateAdornment", { adornmentSelected:true } ); // to update selection in Summary View
        }

        function labelDragMove( dx, dy, x, y, event ) {
            // Note: dx and dy are positions relative to starting position (not last dragMove)
            let lineSet = this,
                lineElement = lineSet.getLineElement(),
                textElement = lineSet.getNonOverlappingLabel(),
                textRect = lineSet.getLabelBackgroundRect(),
                connectorElement = lineSet.getConnectorLine(),
                isXAxis = textElement.data( "isXAxis" ),
                widgetRect = textElement.data( "widgetRect" ),
                viewPt1 = textElement.data( "viewPt1" ),
                viewPt2 = textElement.data( "viewPt2" ),
                lineColor = lineElement.attr( "stroke" );

            // update the text element
            textElement.attr( { x: textElement.data( "startX" ) + dx, y: textElement.data( "startY" ) + dy } );

            // update the connector line and text background elements
            let labelRect = Dyn.PlotLabelsUtil.getLabelRectFromTextElement( textElement ),
                connectorLineRect = Dyn.PlotLabelsUtil.getConnectorLineFromLabelRectToDiagonalLine( labelRect, widgetRect, isXAxis, viewPt1, viewPt2 );
            Dyn.PlotLabelsUtil.saveLabelPositionAndOffset( textElement, labelRect, widgetRect );
            plotElement.labelPositionToModel( textElement );
            textRect.attr( { x: labelRect.x, y: labelRect.y, width: labelRect.width, height: labelRect.height } );
            connectorElement.attr( { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) } );
            plotElement.addGlow( lineElement, lineColor, Dyn.plotUtil.lineGlowWidth ); // update glow position
        }

        function labelDragStart( x, y, event ) {
            //console.log("dragStart");
            if( plotElement.ignoreDragEvents() )
                return;
            let lineSet = this,
                textElement = lineSet.getNonOverlappingLabel(),
                lineIndex = textElement.data( "lineIndex" );
            //textElement.data("drag",true);
            textElement.data( "startX", textElement.attr( "x" ) );
            textElement.data( "startY", textElement.attr( "y" ) );
            textElement.attr( "cursor", Dyn.plotUtil.cursorMove );
            plotElement.selectAdornment( lineIndex );
        }

        function labelDragEnd( event ) {
        }

        function caseFilter( params, caseIndex, xValue, yValue ) {
            // return true of case should be included in least squares calculation
            // if params.categoryAttribute is non-null, filter will filter out cases that don't match params.categoryIndex
            var categoryIndex = (params.categoryAttribute ? getCategoryIndex( caseIndex, params.categoryAttribute ) : 0);
            return (params.dataSet.isCaseNonMissingNonFiltered( caseIndex ) && // exclude filtered and missing cases (determined by dot plot)
                Dyn.mathUtil.isFinite( xValue ) &&  // exclude non-numeric X values
                Dyn.mathUtil.isFinite( yValue ) &&    // exclude non-numeric Y values
                (categoryIndex >= 0 && categoryIndex === params.categoryIndex)
            );
        }

        function getCategoryIndex( caseIndex, legendAttribute ) {
            // NOTE: category index can be -1 if case not in any categories, which will be true for cases with missing value in legend attribute.
            // Caller should then exclude this point from any of the lines
            return legendAttribute.stats.categories.indexOf( legendAttribute.cases[caseIndex] );
        }

        function addToMeasures( lineID, /*xAttrID, yAttrID, xBinIndex, yBinIndex, cBinIndex,*/ name, value ) {
            let oneMeasure = { setID: "leastSq", lineID:lineID, measureName:name, value:value, };
            //if( xAttrID ) oneMeasure.attributeID = xAttrID;
            //if( yAttrID ) oneMeasure.attributeID = yAttrID;
            plotMeasures.addToMeasuresArray( measuresArray, oneMeasure /*, xBinIndex, yBinIndex, cBinIndex */);
        }

        function addLineMeasures( lineIndex, textParams, useCategories,
                                  /*numXAttributes, numYAttributes, xAttr, yAttr,*/ lineColor ) {
            // add measures in display order (and match the equation order (y = mx + b)), for Summary View
            var lineModel = plotElement.lineModels[lineIndex],
                //interceptNotAbs = (lineModel.data.intercept < 0 ? "-" : "") + textParams.b,
                //xBinIndex = ((numXAttributes > 1) ? lineModel.xAttrIndex : 0),
                //yBinIndex = ((numYAttributes > 1) ? lineModel.yAttrIndex : 0),
                //cBinIndex = (useCategories ? lineModel.categoryIndex : undefined), // don't specify a category index unless using LSL by Category,
                //xAttrID = ((numXAttributes > 1) ? xAttr.id : ""),
                //yAttrID = ((numYAttributes > 1) ? yAttr.id : ""),
                lineID = lineModel.id;
            if( useCategories ){
                addToMeasures( lineID, Dyn.loc( "Category" ), textParams.category );
            }
            addToMeasures( lineID, Dyn.loc( "Y" ), textParams.y );
            addToMeasures( lineID, Dyn.loc( "Slope" ), textParams.m );
            addToMeasures( lineID, Dyn.loc( "X" ), textParams.x );
            addToMeasures( lineID, Dyn.loc( "Intercept" ), textParams.interceptNotAbs );
            addToMeasures( lineID, Dyn.loc( "r" ), textParams.r );
            addToMeasures( lineID, Dyn.loc( "r^2" ), textParams.rsquared );
            if( textParams.sumOfSquares ){
                addToMeasures( lineID, Dyn.loc( "Sum of Squares" ), textParams.sumOfSquares );
            }
            addToMeasures( lineID, plotMeasures.getNameOfColorMeasure(), lineColor );
        }

        // calculate line of best fit only if both axis are numerical
        let isDualAxis = Boolean( yAxisView.secondAxis ? yAxisView.secondAxis.isNumericalActiveAxis() : false );
        if( !(xAxisView.isNumberLineAxis() && yAxisView.isNumberLineAxis()) ){
            Dyn.plotUtil.removeAndEmptyArrayOfElements( leastSqAdorns );
            return;
        } else if( leastSqAdorns.length < this.kNumInitialAdornments ){
            // make a context menu button that will be repositioned to the selected line
            leastSqAdorns.push( this.makeContextMenuButton().hide() );
            Dyn.assert( leastSqAdorns.length >= this.kNumInitialAdornments );
        }

        let xAttrIndex,
            yAttrIndex,
            categoryIndex,
            numXAttributes = xAxisView.allAttributes.countActive(),
            numYAttributes = yAxisView.allAttributes.countActiveIncludingDualAxis(),
            dataSet = this.plotShaper.dataSet,
            legendAttribute = dataSet.getSelectedAttribute(),
            hasCategories = Boolean( legendAttribute && legendAttribute.stats.isCategorical() ),
            useCategories = Boolean( this[Dyn.EPlotControl.kControlsLeastSquaresByCategory] && hasCategories ), // note: because of filtering and missing values, there may be 0+ categories.
            numCategoriesInLegend = (useCategories ? legendAttribute.stats.categories.length : 1),
            isShowingLabels = this.plotShaper.showLabelsOnStats,
            squaresOnLineModels = [];

        // for each pair of numeric attributes in plot
        for( xAttrIndex = 0; xAttrIndex < numXAttributes; ++xAttrIndex ){
            for( yAttrIndex = 0; yAttrIndex < numYAttributes; ++yAttrIndex ){
                for( categoryIndex = 0; categoryIndex < numCategoriesInLegend; ++categoryIndex ){

                    // get m, b, r2
                    let xAttr = xAxisView.getActiveAttribute( xAttrIndex ),
                        yAttr = yAxisView.getActiveAttribute( yAttrIndex ),
                        y1or2AxisView = yAxisView.getSubviewForActiveAttribute( yAttrIndex ),
                        filterParams = {
                            dataSet: dataSet,
                            categoryAttribute: useCategories ? legendAttribute : null,
                            categoryIndex: categoryIndex
                        },
                        leastSquares = Dyn.plotUtil.findLineByLeastSquares( xAttr, yAttr, caseFilter, filterParams );

                    // skip this line if not enough points to make a line
                    if( !(isFinite( leastSquares.slope ) && isFinite( leastSquares.intercept )) ){
                        continue;
                    }

                    // {x1, y1}, {x2, y2}
                    let intercepts = Dyn.plotUtil.lineToAxisIntercepts( leastSquares.slope, leastSquares.intercept, xAxisView, y1or2AxisView );
                    Dyn.plotUtil.addAxisInterceptsViewPts( intercepts, xAxisView, y1or2AxisView ); // add view coordinates
                    let viewPt1 = intercepts.viewPt1,
                        viewPt2 = intercepts.viewPt2,
                        pathString = "M" + viewPt1.x.toFixed( 1 ) + "," + viewPt1.y.toFixed( 1 ) +
                            "L" + viewPt2.x.toFixed( 1 ) + "," + viewPt2.y.toFixed( 1 );
                    let decimals = new Dyn.mathUtil.slopeInterceptDecimalPlaces( xAttr, yAttr );
                    let textParams = {
                            yAxis: isDualAxis ? (y1or2AxisView === yAxisView ? 'y1' : 'y2') : 'y',
                            y: yAttr.name,
                            x: xAttr.name,
                            m: decimals.slopeString( leastSquares.slope, yAttr ),
                            interceptNotAbs: decimals.interceptNotAbs( leastSquares.intercept, yAttr ),
                            b: decimals.absInterceptString( leastSquares.intercept, yAttr ),
                            plusMinus: decimals.interceptPlusMinusString( leastSquares.intercept ),
                            r: decimals.rValueString( leastSquares.r, yAttr ),
                            rsquared: decimals.rValueString( leastSquares.r2, yAttr ),
                        },
                        tipText = Dyn.loc( "{{y}} = ({{m}}) {{x}} {{plusMinus}} {{b}}", textParams ),
                        labelText = Dyn.loc( "{{yAxis}} = ({{m}}) x {{plusMinus}} {{b}} | r = {{r}}", textParams );
                    tipText += '\n' + Dyn.loc( "r = {{r}}", textParams );
                    if( this.plotShaper.showSquaresOnLines ){
                        leastSquares.sumOfSquares = Dyn.plotUtil.calcSumOfSquares( xAttr, yAttr, caseFilter, filterParams, leastSquares );
                        if( Number.isFinite( leastSquares.sumOfSquares ) ){
                            textParams.sumOfSquares = yAttr.axisValueToString( leastSquares.sumOfSquares );
                            tipText += '\n' + Dyn.loc( "sum of squares = {{sumOfSquares}}", textParams );
                        }
                    }

                    if( useCategories && legendAttribute ){
                        // add "category: " to formula to identify which category line belongs to. Attribute name is already present.
                        textParams.category = legendAttribute.axisValueToString( legendAttribute.stats.categories[categoryIndex] );
                        tipText = Dyn.loc( "{{category}}: {{formula}}", { category: textParams.category, formula: tipText } );
                        labelText = Dyn.loc( "{{category}}: {{formula}}", { category: textParams.category, formula: labelText } );
                    }

                    // add a line to our line adornments
                    let lineIndex = newNumLines++,
                        lineColor = this._calcLineColor(
                            { index: xAttrIndex, numAttrs: numXAttributes, attr: xAttr },
                            { index: yAttrIndex, numAttrs: numYAttributes, attr: yAttr },
                            { index: categoryIndex, attr: legendAttribute, hasCategories: hasCategories,
                                numCategories: ((legendAttribute && useCategories) ? numCategoriesInLegend : 0) },
                            lineIndex );
                    this.lineModels.push( new LeastSquaresLineModel( xAttrIndex, yAttrIndex, categoryIndex, leastSquares, lineIndex ));
                    let lineSet = this._getLineAdornment( leastSqAdorns, lineIndex ),
                        lineElement = lineSet ? lineSet.getLineElement() : null,
                        textRect = lineSet ? lineSet.getLabelBackgroundRect() : null,
                        connectorLine = lineSet ? lineSet.getConnectorLine() : null,
                        textElement = lineSet ? lineSet.getNonOverlappingLabel() : null;
                    const clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                        animationTimeMS = (noAnimation ? 0 : Dyn.plotUtil.adornAnimationTimeMs),
                        animationEasing = Dyn.plotUtil.adornAnimationEasing;
                    let cell = this.plotShaper.bins.cells[0],
                        widgetRect = (new Dyn.mathUtil.Rect()).setFromTwoPoints( viewPt1, viewPt2 ),
                        wantHorizontalConnectorLine = Dyn.PlotLabelsUtil.shouldConnectorLineBeHorizontal( cell, widgetRect, viewPt1, viewPt2 ),
                        isNewLine = ! lineSet;

                    if( lineSet ){
                        // updating LSL line components
                        Dyn.assert( lineElement );
                        lineElement
                            .animate( { // move line to new path location, with proper color
                                    "path": pathString,
                                    stroke: lineColor,
                                    'stroke-width': Dyn.plotUtil.refLineWidth
                                },
                                animationTimeMS, animationEasing );
                        textElement
                            .data( "viewPt1", viewPt1 )
                            .data( "viewPt2", viewPt2 )
                            .attr( { text: labelText, "font-size": Dyn.preferences.getFontSize() } ); // so setLabelPositionFirstPass() has the current text size
                        if( isShowingLabels ){
                            this.modelToLabelPosition( textElement, cell, lineIndex );
                            let labelRect = Dyn.PlotLabelsUtil.setLabelPositionFirstPass( this, textElement, wantHorizontalConnectorLine, cell, widgetRect ),
                                connectorLineRect = Dyn.PlotLabelsUtil.getConnectorLineFromLabelRectToDiagonalLine( labelRect, widgetRect, wantHorizontalConnectorLine, viewPt1, viewPt2 );
                            textRect.animate( {
                                x: labelRect.x,
                                y: labelRect.y,
                                width: labelRect.width,
                                height: labelRect.height,
                                fill: lineColor,
                            }, animationTimeMS, animationEasing );
                            textElement.animate( {
                                x: labelRect.centerX(),
                                y: labelRect.centerY(),
                            }, animationTimeMS, animationEasing );
                            connectorLine.animate( {
                                stroke: lineColor,
                                path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect )
                            }, animationTimeMS, animationEasing );
                        }
                    } else {
                        // create a new line, with label that is either shown or hidden
                        lineSet = plotViewPaper.set();
                        lineElement = plotViewPaper.path( pathString )
                            .hover( hoverIn.bind( lineSet ), hoverOut.bind( lineSet ) )
                            .click( lineClick.bind( lineSet ) )
                            .mousemove( mouseMove )
                            .attr( { stroke: lineColor, 'stroke-width': 0 } )
                            .animate( { 'stroke-width': Dyn.plotUtil.refLineWidth }, // 0 to refLineWidth animation when created
                                animationTimeMS, animationEasing );
                        textRect = plotViewPaper.rect( 0, 0, 0, 0, Dyn.plotUtil.statLabelRoundRectRadius )
                            //.data( "layerNum", 1 ) // use setAdornmentLayer() instead
                            .attr( { opacity: 0, fill: lineColor, stroke: "none" } )
                            .animate( { opacity: Dyn.plotUtil.statLabelOpacity }, animationTimeMS, animationEasing );
                        connectorLine = plotViewPaper.path( "M0,0" )
                            //.data( "layerNum", 1 ) // use setAdornmentLayer() instead
                            .attr( {
                                stroke: lineColor,
                                'stroke-width': Dyn.plotView.statLabelConnectorLineWidth,
                                opacity: 0,
                                'clip-rect': clipRectString
                            } )
                            .animate( { opacity: 1 }, animationTimeMS, animationEasing );
                        textElement = plotViewPaper.text( 0, 0, labelText )
                            .data( "viewPt1", viewPt1 )
                            .data( "viewPt2", viewPt2 )
                            //.data( "layerNum", 1 ) // use setAdornmentLayer() instead
                            .attr( {
                                opacity: 0, fill: Dyn.plotUtil.statLabelTextColor, "text-anchor": "middle",
                                "font-size": Dyn.preferences.getFontSize(), "font-family":Dyn.plotUtil.statLabelFontFamily,
                                cursor: Dyn.plotUtil.cursorMove
                            } )
                            .animate( { opacity: 1 }, animationTimeMS, animationEasing );

                        // position the label, background rect, and connector line
                        this.modelToLabelPosition( textElement, cell, lineIndex );
                        let labelRect = Dyn.PlotLabelsUtil.setLabelPositionFirstPass( this, textElement, wantHorizontalConnectorLine, cell, widgetRect ),
                            connectorLineRect = Dyn.PlotLabelsUtil.getConnectorLineFromLabelRectToDiagonalLine( labelRect, widgetRect, wantHorizontalConnectorLine, viewPt1, viewPt2 );
                        textElement.attr( { x: labelRect.centerX(), y: labelRect.centerY() } )
                            .hover( hoverIn.bind( lineSet ), hoverOut.bind( lineSet ) )
                            .drag( labelDragMove.bind( lineSet ), labelDragStart.bind( lineSet ), labelDragEnd.bind( lineSet ) );
                        textRect.attr( {
                            x: labelRect.x,
                            y: labelRect.y,
                            width: labelRect.width,
                            height: labelRect.height
                        } );
                        connectorLine.attr( { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) } );
                        Dyn.plotUtil.disableMouseOverEvents( connectorLine );
                        this.plotShaper.setAdornmentLayer( 1, textRect, textElement, connectorLine );

                        // add visible elements from bottom layer to top
                        lineSet.push( lineElement, connectorLine, textRect, textElement );
                        lineSet.getLineElement = function() {
                            return this[0];
                        };
                        lineSet.getConnectorLine = function() {
                            return this[1];
                        };
                        lineSet.getLabelBackgroundRect = function() {
                            return this[2];
                        };
                        lineSet.getNonOverlappingLabel = function() {
                            return this[3];
                        };
                        lineSet.getNthSquare = function( n ) {
                            return (n < (this.length - 4)) ? this[4 + n] : null;
                        };
                        lineSet.getNumSquares = function(){ return Math.max(0, this.length-4 ); }
                        //lineSet.getDashedLine() = --not used for leastSquaresElement
                        //lineSet.getNonOverlappingWidgetRect = function() {...} -- not used for leastSquaresElement
                        leastSqAdorns.push( lineSet );
                    }
                    lineElement.data( "tooltip", tipText );
                    lineElement.data( "menuPos", this.calcMenuButtonPosition( viewPt1, viewPt2, cell ) );
                    lineElement.data( "color", lineColor );
                    textElement.data( "lineIndex", lineIndex );
                    this.labelPositionToModel( textElement );

                    // update selection
                    if( isNewLine && lineIndex === 0 && this.selectedLineIndex === -1 ) {
                        this.selectAdornment( lineIndex ); // select and show squares if lines appearing for first time
                    } else if( lineIndex === this.selectedLineIndex ){
                        this.updateSelectionVisuals(); // reposition close box
                    }

                    // show or hide label and connector line
                    Dyn.plotUtil.showOrHideElement( lineSet.getLabelBackgroundRect(), isShowingLabels );
                    Dyn.plotUtil.showOrHideElement( lineSet.getConnectorLine(), isShowingLabels );
                    Dyn.plotUtil.showOrHideElement( lineSet.getNonOverlappingLabel(), isShowingLabels );

                    // Show Squares or Hide Squares
                    let squaresModel = new Dyn.SquaresOnLineModel( xAxisView, yAxisView, xAttrIndex, yAttrIndex,
                            leastSquares, filterParams, lineSet, lineColor );
                    squaresOnLineModels.push( squaresModel );
                    squaresModel.updateSquares( lineIndex === this.showSquaresLineIndex,
                        caseFilter, this, lineElement );

                    // add measures for Summary View
                    addLineMeasures( lineIndex, textParams, useCategories, lineColor );
                }
            }
        }
        Dyn.assert( newNumLines <= leastSqAdorns.length );

        // remove old lines from our list of adornments
        let newNumAdornments = this.kNumInitialAdornments + newNumLines;
        for( let oldNumAdornments = leastSqAdorns.length; oldNumAdornments > newNumAdornments; --oldNumAdornments ){
            if( oldNumAdornments > newNumAdornments ){
                leastSqAdorns.pop().remove();
            }
        }
        // remove old coordinates from our list of Stat Label positions
        if( this.normalizedCoords ){
            this.normalizedCoords.length = newNumLines;
        }
        Dyn.assert( newNumAdornments === leastSqAdorns.length );
        if( this.updateShowSquares() && Dyn.mathUtil.isInIntegerRange( this.showSquaresLineIndex, 0, squaresOnLineModels.length )) {
            // shift show squares to a new line if old line no longer exists
            let squaresModel = squaresOnLineModels[this.showSquaresLineIndex],
                lineElement = squaresModel.squaresSet.getLineElement();
            squaresModel.updateSquares( true, caseFilter, this, lineElement );
        }
    };

    /** compute the color of the given Least Squares Line */
    _calcLineColor( xData, yData, catData, lineIndex ) {
        if( this.lineColors[lineIndex] ){ // note: array is sparse, don't use if undefined color string
            return this.lineColors[lineIndex]; // user-specified color from _saveLineColor()
        } else if( catData.numCategories > 1 ){
            // if multiple categories (and possibly multi-attribute), different line color for each category
            let legendAttribute = catData.attr;
            Dyn.assertArrayIndex( legendAttribute.stats.categories, catData.index, "invalid category index" );
            return Dyn.colorUtil.calcCategoryColor(
                legendAttribute.statsPreFilter,
                legendAttribute.color,
                legendAttribute.stats.categories[catData.index],
                false,
                legendAttribute.colorMap,
                legendAttribute.isSortAscending ).colorString;
        } else if( (xData.numAttrs > 1 || yData.numAttrs > 1) && !catData.hasCategories ){
            // different line color for each attribute in multi-attribute plot
            let isXAxisMultiple = (xData.numAttrs > 1),
                oneOfMultipleAttributes = isXAxisMultiple ? xData.attr : yData.attr;
            Dyn.assert( isXAxisMultiple ? (yData.numAttrs <= 1) : (xData.numAttrs <= 1), "expect only one axis to be multi-attribute" );
            return oneOfMultipleAttributes.multiAttrStrokeColor.colorString;
        } else {
            // either (a) one attribute only -- no need for a line color
            // or (b) one category only -- don't use the category color, use our generic color for readability against dots
            // or (c) multiple attributes and multiple categories but only one line per attribute -- lines cannot match either
            //      color key or attribute color, so use neither.
            return Dyn.plotUtil.leastSquaresColor;
        }
    }

    /**
     * Save a user-specified color string for later restore. Note that array can be sparse.
     * See also this._calcLineColor */
    _saveLineColor( lineIndex, colorString ) {
        Dyn.assert( typeof colorString === 'string' );
        // if( !this.lineColors ){
        //     this.lineColors = [];
        // }
        this.lineColors[lineIndex] = colorString;
    }

    /**
     * Update the color of the LSL line elements.
     * See also _calcLineColor(), _saveLineColor() */
    _updateLineColor( lineSet, colorString ) {
        Dyn.assert( typeof colorString === 'string' );
        let lineElement = lineSet.getLineElement(),
            connectorLine = lineSet.getConnectorLine(),
            textRect = lineSet.getLabelBackgroundRect();

        lineElement.attr( { stroke: colorString } ); // update line
        connectorLine.attr( { stroke: colorString } );
        textRect.attr( { fill: colorString } );
        lineSet.forEach( el => {
            if( el.type === 'rect' ){ // square colors
                el.attr( { stroke: colorString } );
            }
        } )
    }

    /** get the set of adornments (visible plot elements) for the given least squares line */
    _getLineAdornment( leastSqAdorns, lineIndex ) {
        let adornmentIndex = this.kNumInitialAdornments + lineIndex;
        if( Dyn.mathUtil.isInIntegerRange( adornmentIndex, this.kNumInitialAdornments, leastSqAdorns.length )) {
            return leastSqAdorns[adornmentIndex];
        }
        return null;
    }

    _getContextMenuButton( leastSqAdorns ) {
        let contextMenu = leastSqAdorns[0];
        return contextMenu || null;
    }

    /**
     * {OVERRIDE} Deselect any selected adornments in this plot element.
     */
    clearSelectedAdornments() {
        let oldSelection = this.selectedLineIndex,
            leastSqAdorns = this.getAdornmentsArray(),
            contextMenu = this._getContextMenuButton( leastSqAdorns );
        // hide color selector that is shown by selectAdornment()
        if( contextMenu ){
            contextMenu.hide();
        }
        this.selectedLineIndex = -1;
        return( oldSelection !== this.selectedLineIndex );
    }

    /**
     * Select an adornment.  See clearSelectedAdornments for inverse.
     * @param lineIndex - if defined, the index of the line to select
     * @param data - if defined, the IDs of the line to select
     */
    selectAdornment( lineIndex, data ) {
        if( data ) {
            if( data.tableSetID === 'leastSq' && data.lineID ) {
                // select the LSL line that matches the summary view row's lineID (e.g. 'LSL-3')
                lineIndex = this.lineModels.findIndex( (m) => m.id===data.lineID );
            }
        }
        let oldSelection = this.selectedLineIndex,
            leastSqAdorns = this.getAdornmentsArray(),
            lineSet = this._getLineAdornment( leastSqAdorns, lineIndex );
        if( lineSet ){
            this.selectedLineIndex = lineIndex;
            this.updateSelectionVisuals();

            // update show squares to match selected line
            if( this.plotShaper.showSquaresOnLines ){
                this.plotShaper.showHideSquaresOnLines( true, this ); // turn on for this element, off for others
            } else {
                this.showSquaresLineIndex = -1;
            }
            this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
        } else {
            this.clearSelectedAdornments();
        }
        return( oldSelection !== this.selectedLineIndex );
    }

    /** Update the visible elements to match the selection, or hide if no line selected */
    updateSelectionVisuals() {
        let leastSqAdorns = this.getAdornmentsArray(),
            lineSet = this._getLineAdornment( leastSqAdorns, this.selectedLineIndex ),
            contextMenu = this._getContextMenuButton( leastSqAdorns );
        if( lineSet ){
            let lineElement = lineSet.getLineElement(),
                centerPt = lineElement.data( "menuPos" ),
                iconSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
                expandedClip = iconSize * 0.3, // expand clip area a little to not clip context menu
                clipRectString = Dyn.plotView.getPlotInteriorClipRect( expandedClip, expandedClip );

            // show the color selector when selected, but make sure it is clipped to plot interior, and matching the selected line
            if( contextMenu ){
                contextMenu.getSymbolElement().attr( { 'clip-rect': clipRectString } );
                contextMenu.transform( "t" + (centerPt.x) + "," + (centerPt.y) );
                contextMenu.show();
            }
        } else { // nothing selected
            if( contextMenu ){
                contextMenu.hide();
            }
        }
    }

    /** Does this element have a selected line with the given ID?  */
    isAdornmentSelected( tableSetID, adornmentID ) {
        return(
            (tableSetID === 'leastSq')
            && Dyn.mathUtil.isInIntegerRange( this.selectedLineIndex, 0, this.lineModels.length )
            && (adornmentID === this.lineModels[this.selectedLineIndex].id ));
    }

    /**
     * Calculate the center position of context menu button, based on the position
     * of the least squares line in the plot.
     * @param viewPt1 - line endpoint in view coordinates
     * @param viewPt2 - line endpoint in view coordinates
     * @param cellRect - plotting area of line in view coordinates
     * @return {{x,y}} - position of center of color selector icon in the plot view
     */
    calcMenuButtonPosition( viewPt1, viewPt2, cellRect ) {
        let iconSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
            pts = [viewPt1, viewPt2],
            p = (pts[0].x > pts[1].x) ? pts[0] : pts[1], // pick line endpoint to right
            isNearRightEdgeOfCell = ((p.x - cellRect.x) > cellRect.width * 0.9),
            isNearTopEdgeOfCell = ((p.y - cellRect.y) < cellRect.height * 0.1),
            pos;
        //console.log( "calcMenuButtonPosition", isNearRightEdgeOfCell, isNearTopEdgeOfCell );
        return (isNearRightEdgeOfCell && !isNearTopEdgeOfCell) ?
            { x: p.x, y: p.y - iconSize } : // position above right-most line endpoint
            ((isNearRightEdgeOfCell && isNearTopEdgeOfCell) ?
                { x: p.x, y: p.y + iconSize } : // position belw right-most line endpoint
                { x: p.x + iconSize, y: p.y }); // position to right of right-most line endpoint
    }

    /**
     * Make a color selector for the Least Squares Line.
     * Caller must position the color selector to the currently selected line, or hide.
     * @returns {*} -- Raphael color selector element
     */
    makeContextMenuButton() {

        function buttonClick( e ) {
            // handler to pick line color, called by click or keydown event
            let lineIndex = plotElement.selectedLineIndex,
                lineSet = plotElement._getLineAdornment( plotElement.getAdornmentsArray(), plotElement.selectedLineIndex );
            if( lineSet ){
                let lineElement = lineSet.getLineElement(),
                    lineColor = lineElement.attr( "stroke" ),
                    xy = Dyn.plotUtil.clickOrKeyboardEventToPaperYX( e, lineElement );
                setTimeout( () => { // delayed call is required to show when e.type==='keykdown', reason unknown
                    ContextMenuUtil.show( {x:xy.x, y:xy.y}, { colorHex: lineColor, colorsArray: Dyn.plotUtil.annotationColors, isShowSquaresOnLinesEnable: true }, ( color ) => {
                        if( color && color.hex ){
                            //this.attr( { fill: color.hex } ); // update color selector color
                            plotElement._updateLineColor( lineSet, color.hex ); // update line elements' color
                            plotElement._saveLineColor( lineIndex, color.hex ); // update model for save/restore
                            lineElement.data( "color", color.hex ); // save for hover/select changes
                            mediator.publish("plot:updateAdornment", {}); // to update color in Summary View
                        }
                    }, Dyn.plotUtil.leastSquareContextMenuID, Dyn );
                }, 100 );
            }
        }

        var plotElement = this,
            tooltipAndAriaLabel = Dyn.loc( "least squares line menu" ),
            buttonSet = Dyn.plotUtil.makeContextMenuButton(
                this.plotShaper.adornments.paper,
                buttonClick,
                {}, // no key handler; delete key ignored for LSRL
                tooltipAndAriaLabel, Dyn.plotUtil.leastSquareContextMenuID );
        this.plotShaper.setAdornmentLayer( 2, buttonSet );
        return buttonSet;
    }

    /**
     * @param wantToShowSquares {Boolean}
     */
    setShowSquares( wantToShowSquares ) {
        if( wantToShowSquares ){
            // if want to show squares, indicate which of 1+ lines should show squares
            this.showSquaresLineIndex = (this.selectedLineIndex >= 0) ? this.selectedLineIndex : 0;
        } else { // else turn off show squares
            this.showSquaresLineIndex = -1;
        }
    }

    /** Does this plot element have lines that can show squares? */
    canShowSquares( requireSelectedLine ) {
        Dyn.assert( this[Dyn.EPlotControl.kControlsShowSquares]);
        let numLines = this.lineModels.length;
        return( requireSelectedLine ?
            ((numLines > 0) && Dyn.mathUtil.isInIntegerRange( this.selectedLineIndex, 0, numLines)) :
            (numLines > 0));
    }

    /**
     * Check for a valid showSquaresOnLines setting. Should be called when the number of lines
     * may have changed (e.g. after data or axis change).
     * @return {boolean} - true if this.showSquaresLineIndex has changed
     */
    updateShowSquares() {
        let oldShowSquaresLineIndex = this.showSquaresLineIndex,
            lastLineIndex = this.lineModels.length - 1;
        if( this.showSquaresLineIndex > lastLineIndex ) {
            // update show squares to match selected line
            if( this.plotShaper.showSquaresOnLines ) {
                this.selectedLineIndex = Math.min( this.selectedLineIndex, lastLineIndex ); // first fix selection
                let useThis = (lastLineIndex >= 0) ? this : null; // switch to this element if we have other lines
                this.plotShaper.showHideSquaresOnLines( true, useThis );
            } else {
                this.showSquaresLineIndex = -1;
            }
        }
        return( this.showSquaresLineIndex !== oldShowSquaresLineIndex );
    }

    } // class LeastSquaresElement
    Dyn.LeastSquaresElement = LeastSquaresElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsLeastSquares, Dyn.LeastSquaresElement );
    Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsOldBestFit, function( savedData, partialPlotState ) {
        return Dyn.LeastSquaresElement; // convert an old best fix function as Least Squares on restore
    } );
    //======= end of Dyn.LeastSquaresElement definitions

    class SquaresOnLineModel {
        /**
         * A utility model of the data needed to show squares on a single line.  Its job is to add or remove
         * visible squares to a single Movable Line or Least Squares Line ("Show Squares" or "Hide Squares").
         * Squares are added and removed from the given "squaresSet", part of the Adornments for the plot element.
         * Note that each constructed SquaresOnLineModel has the custom data for that particular line,
         * then for updateSquaress we pass in the common parameters common to all lines.
         */
        constructor( xAxisView, yAxisView, xAttrIndex, yAttrIndex, lineEquation, filterParams, squaresSet, color ) {
            this.xAxisView = xAxisView;
            this.yAxisView = yAxisView.getSubviewForActiveAttribute( yAttrIndex );
            this.xAttrIndex = xAttrIndex; // reference to x-axis attribute for line
            this.yAttrIndex = yAxisView.getSubviewIndexForActiveAttribute( yAttrIndex ); // reference to y-axis attribute for line
            this.slope = lineEquation.slope;
            this.intercept = lineEquation.intercept;
            this.filterParams = filterParams; // data used by filterFunction to select cases for this line
            this.squaresSet = squaresSet; // Raphael set() added to Adornments for the given plot element
            this.color = color; // color of squares
            Dyn.assert( this.xAxisView.getActiveAttribute( this.xAttrIndex));
            Dyn.assert( this.yAxisView.getActiveAttribute( this.yAttrIndex));
        }

        /** Get Y-axis value of line, y=f(x) a.k.a y=mx+b */
        fx( x ) {
            return (this.slope * x) + this.intercept;
        }

        /**
         * Compute the width of the square from the height, in screen coordinates.
         * Note that both values can be positive or negative, determining the placement
         * of the square, so that the square does not intersect the line.
         * negative height = data point is below line, positive=above line.
         * negative width = squares goes to left of data point, positive=to right.
         * The square should be places so it does not intersect with the line.
         * @param isPositiveSlope {boolean} -- constant from isPositiveSlopeOnScreen()
         * @param height {number} -- positive or negative distance from line to point
         */
        widthOfSquareFromHeight( isPositiveSlope, height ) {
            let isPositiveHeight = (height > 0);
            return (isPositiveSlope==isPositiveHeight) ? Math.abs(height) : -Math.abs(height);
        }
        /** is movable/least-squares line slope positive *in screen coordinates*? */
        isPositiveSlopeOnScreen() {
            return this.yAxisView.dataToPixel( this.fx(0)) < this.yAxisView.dataToPixel( this.fx(1));
        }

        /** add or remove visible squares to a least squares line.
         * Our constructor has the unique paramaters for this line, we pass in parameters in common to multiple lines.
         * @param showSquares {boolean} -- show or hide the squares?
         * @param filterFunction {*} -- which cases are on this line (e.g. for least squares line by category)
         * @param plotElement {Dyn.PlotElement} -- which plot element (Dyn.LeastSquaresElement, Dyn.DiagRefLineElementY2, etc.)
         * @param lineElement {Raphael path} -- visible SVG path for movable line/least squares line
         */
        updateSquares( showSquares, filterFunction, plotElement, lineElement ) {
            if( ! showSquares ) {
                SquaresOnLineModel.removeSquares( this.squaresSet );
            } else {
                this.updateDashedLine();
                const clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                    xCases = this.xAxisView.getActiveAttribute( this.xAttrIndex).cases,
                    yCases = this.yAxisView.getActiveAttribute( this.yAttrIndex).cases,
                    numCases = Math.min( xCases.length, yCases.length ),
                    isPositiveSlope = this.isPositiveSlopeOnScreen();
                let set = this.squaresSet,
                    r = new Dyn.mathUtil.Rect(),
                    newNumSquares = 0;
                for (let c = 0; c < numCases; c++) {
                    let x = xCases[c],
                        y = yCases[c];
                    // skipping non-numeric and missing and filtered cases (see also
                    if( filterFunction( this.filterParams, c, x, y )) {
                        // get corners of square in view coordinates
                        r.x = this.xAxisView.dataToPixel( x );
                        r.y = this.yAxisView.dataToPixel( y );
                        let y2 = this.fx(x);
                        r.height = this.yAxisView.dataToPixel( y2 ) - r.y;
                        r.width = this.widthOfSquareFromHeight( isPositiveSlope, r.height );
                        r.normalize(); // adjust rect so height and width are positive values
                        // add or update visible rectangle to match
                        let squareEl = set.getNthSquare( newNumSquares );
                        if( squareEl ) {
                            squareEl.attr({ x:r.x, y:r.y, width:r.width, height:r.height });
                        } else {
                            squareEl = set.paper.rect( r.x, r.y, r.width, r.height )
                                .hover( hoverIn, hoverOut )
                                .data("caseIndex", c )
                                .data("attrIndex", Math.max( this.xAttrIndex, this.yAttrIndex ));
                                //.data( "tooltip", Dyn.loc("Case: {{n}}", {n:c+1}));
                            set.push( squareEl );
                        }
                        squareEl.attr({ stroke:this.color, 'stroke-width':Dyn.plotUtil.showSquaresLineWidth, 'clip-rect':clipRectString });
                        ++newNumSquares;
                    }
                }
                // remove unused squares
                while( set.getNumSquares() > newNumSquares ) {
                    set.pop().remove();
                }
                Dyn.assert( set.getNumSquares() === newNumSquares, "Least Squares number mismatch" );
            }

            function hoverIn( e ) {
                let squareElement = this,
                    lineColor = lineElement.attr( "stroke" ),
                    caseIndex = squareElement.data("caseIndex"),
                    attrIndex = squareElement.data("attrIndex"),
                    caseIcon = Dyn.plotView.getCaseIconForGlow( caseIndex, attrIndex );
                // if( Dyn.toolTip.isMyElement( squareElement ) ){
                //     Dyn.toolTip.update( squareElement, e, {} );
                // } else {
                //     Dyn.toolTip.show( squareElement, e, {} );
                // }
                if( caseIcon ) {
                    caseIcon.toFront();
                    plotElement.addGlow( [caseIcon, lineElement, squareElement], lineColor, Dyn.plotUtil.glowWidth );
                } else {
                    plotElement.addGlow( [lineElement, squareElement], lineColor, Dyn.plotUtil.glowWidth );
                }
            }

            function hoverOut( /* e  */ ) {
                let squareElement = this;
                // if( Dyn.toolTip.isMyElement( squareElement ) ){ // tip element from hoverIn()
                //     Dyn.toolTip.hide();
                // }
                plotElement.removeGlow();
                plotElement.plotShaper.layerAdornments(); // undo layering from hoverIn()
            }
        }

        /**
         * add the optional dashed line, which extends Movable Line to edges of plotting area.
         * See also removeSquares()
         */
        updateDashedLine() {
            let set = this.squaresSet; // Raphael set that holds squares and related visible elements
            if( set.getDashedLine ) {
                let intercepts = Dyn.plotUtil.lineToAxisIntercepts( this.slope, this.intercept, this.xAxisView, this.yAxisView );
                Dyn.assertFiniteNumeric( intercepts.pt1.x );
                Dyn.assertFiniteNumeric( intercepts.pt1.y );
                Dyn.assertFiniteNumeric( intercepts.pt2.x );
                Dyn.assertFiniteNumeric( intercepts.pt2.y );
                Dyn.plotUtil.addAxisInterceptsViewPts( intercepts, this.xAxisView, this.yAxisView ); // add view coordinates
                let pathString = "M" + intercepts.viewPt1.x.toFixed( 1 ) + "," + intercepts.viewPt1.y.toFixed( 1 ) +
                        "L" + intercepts.viewPt2.x.toFixed( 1 ) + "," + intercepts.viewPt2.y.toFixed( 1 );
                // remove optional dashed line, which extends Movable Line to edges of plotting area
                let dashedLine = set.getDashedLine();
                if( dashedLine ) {
                    dashedLine.attr({ path:pathString, stroke:this.color });
                } else {
                    dashedLine = set.paper.path( pathString ).attr( 'stroke', this.color );
                    set.push( dashedLine );
                    Dyn.assert( dashedLine === set.getDashedLine(), "Dashed line pushed to set at wrong index, index="+(set.length-1));
                }
                dashedLine.node.setAttribute('stroke-dasharray', Dyn.plotUtil.showSquaresDashArray );
            }
        }

        /** remove squares added with updateSquares() */
        static removeSquares( squaresSet ) {
            if( squaresSet.getDashedLine ) {
                // remove optional dashed line, which extends Movable Line to edges of plotting area
                let dashedLine = squaresSet.getDashedLine();
                dashedLine && dashedLine.attr({ path:"m0,0" });
            }
            while( squaresSet.getNumSquares() > 0 ) {
                squaresSet.pop().remove(); // remove visible Raphael rect
                // Warning: pop() reduces .length but element not immediately removed from array by Raphaeljs
            }
            Dyn.assert( squaresSet.getNumSquares() === 0 );
        }
    } // end Dyn.SquaresOnLineModel
    Dyn.SquaresOnLineModel = SquaresOnLineModel;

} // end wrapDynLeastSquaresElement
